import Anthropic from '@anthropic-ai/sdk';

const ANTHROPIC_API_KEY = import.meta.env.VITE_ANTHROPIC_API_KEY;

export const premiumPrompt = `You are a UK legal expert specializing in residential tenancy agreements under the 2025 Renters' Rights Bill. Generate a comprehensive PREMIUM VERSION UK tenancy agreement (25-30 pages, approximately 10,000-12,000 words) that is fully compliant with the Renters' Rights Bill 2024 with exhaustive professional-grade documentation, emphasizing periodic (rolling) tenancies and complete abolition of Section 21.

CRITICAL COMPLIANCE REQUIREMENTS - RENTERS' RIGHTS BILL 2024:
1. PERIODIC TENANCY FRAMEWORK: This is a periodic tenancy continuing on a rolling monthly basis until ended by proper notice. DO NOT imply any fixed end date or expiration. Include comprehensive legal framework explaining statutory security of tenure.
2. SECTION 21 ABOLISHED: DO NOT include any Section 21 references. Possession can ONLY be sought under Section 8 grounds. Include exhaustive analysis of why Section 21 no longer exists.
3. EXHAUSTIVE SECTION 8 ANALYSIS: Include detailed legal analysis of all Section 8 grounds with:
   - Complete listing of mandatory and discretionary grounds
   - Detailed explanation of 12-month protection period for Grounds 1 and 1A
   - Full court procedures and evidential requirements for each ground
   - Case law references and precedents
   - Plain English summaries for each ground
   - Flowcharts and decision trees for possession processes
   - Appeal rights and procedures
4. COMPREHENSIVE RENT INCREASE MECHANISMS: Maximum ONCE per 12-month period with exhaustive detail:
   - Section 13 notice procedure with statutory forms
   - Market review procedures with evidence standards
   - Tribunal challenge protocols with hearing procedures
   - Comparable property evidence requirements
   - Timeline requirements (minimum 52 weeks, advance notice periods)
   - Market rent determination methodologies
   - Appeal and review processes
5. EXHAUSTIVE PET PROVISIONS: Cannot unreasonably refuse with complete framework:
   - Detailed written consent procedures
   - 28-day statutory response timeline with consequences for non-compliance
   - Comprehensive definition of "reasonable refusal" with legal examples
   - Pet agreement addendum with reasonable conditions
   - Insurance considerations (carefully avoiding banned fees)
   - Pet damage provisions within deposit cap limits
   - Alternative pet insurance frameworks
6. COMPREHENSIVE TENANT RIGHTS SCHEDULE: Include extensive schedules with:
   - Complete security of tenure legal framework
   - Detailed notice rights (2 months for tenant, grounds for landlord)
   - Full repair request procedures with statutory timescales
   - Complete dispute escalation pathways (negotiation → mediation → tribunal → court)
   - Anti-discrimination framework (benefits, children, protected characteristics)
   - Decent Homes Standard compliance matrix with inspection schedules
   - Awaab's Law hazard categories with statutory response timescales
   - Landlord penalty provisions with appeal rights (£7,000 minor, £40,000 serious)
7. NO ZOMBIE CLAUSES: Completely exclude all language about "end of fixed term," "automatic termination," "expiry," "renewal," "guaranteed possession dates," or landlord-led eviction by default.

MANDATORY EXHAUSTIVE SECTIONS:
- COMPLETE SECTION 8 POSSESSION GROUNDS with legal precedents and analysis
- DETAILED RENT INCREASE MECHANISM with market evidence procedures and tribunal protocols
- EXTENSIVE TENANT RIGHTS SCHEDULE with plain English summaries and legal frameworks
- COMPREHENSIVE PET REQUEST PROCEDURE with statutory timelines and reasonable refusal criteria
- FULL DEPOSIT PROTECTION with scheme registration and prescribed information requirements
- DETAILED MAINTENANCE SCHEDULES with statutory timescales and penalty frameworks
- DECENT HOMES STANDARD compliance matrix with inspection and remedy schedules
- AWAAB'S LAW hazard identification with categorization and statutory response requirements
- COMPLETE DISPUTE RESOLUTION FRAMEWORK with escalation pathways and cost implications
- ANTI-DISCRIMINATION PROTECTIONS with legal basis and enforcement mechanisms
- COMPLIANCE DOCUMENTATION with audit trails and record-keeping requirements

CRITICAL FORMATTING REQUIREMENTS:
- Use professional hierarchical structure with numbered sections (1., 2., 3., etc.)
- Use comprehensive subsections (1.1, 1.2) and detailed sub-subsections (1.1.1, 1.1.2, 1.1.3)
- Include article-level numbering for complex clauses (1.1.1(a), 1.1.1(b), 1.1.1(c))
- Add blank lines and separator lines between major sections
- Use BOLD ALL CAPS for major section headings with double separator lines
- Use Bold Title Case for subsection headings
- Include comprehensive Table of Contents with section and page references
- Add detailed SCHEDULES and APPENDICES sections
- Use professional formatting with consistent indentation (4 spaces per level)
- Include bullet points, numbered lists, and lettered sub-points
- Add execution clause with formal signature blocks for all parties and witnesses
- Include document control footer with version, date, and page numbers
- Add cross-references between related sections

PROFESSIONAL FORMAT EXAMPLE:
================================================================================
                    PREMIUM PERIODIC TENANCY AGREEMENT
                              PROFESSIONAL VERSION
            2025 RENTERS' RIGHTS BILL FULLY COMPLIANT - TRIBUNAL READY
================================================================================

DOCUMENT CONTROL
Version: 1.0 | Date: [DATE] | Reference: PTA-PREM-[REF]

TABLE OF CONTENTS
RECITALS ................................................................ 1
1. DEFINITIONS AND INTERPRETATION ....................................... 2
2. PARTIES AND PROPERTY ................................................. 4
   2.1 The Landlord ..................................................... 4
       2.1.1 Identity and Legal Capacity ................................ 4
       2.1.2 Contact Details ............................................ 4
   2.2 The Tenant ....................................................... 5
3. PERIODIC TENANCY GRANT AND LEGAL FRAMEWORK ........................... 6
4. SECTION 8 POSSESSION GROUNDS: COMPLETE ANALYSIS ...................... 10
5. RENT REVIEW: COMPREHENSIVE MECHANISM AND TRIBUNAL PROTOCOLS .......... 18
6. TENANT RIGHTS AND PROTECTIONS: EXHAUSTIVE SCHEDULE ................... 24
7. PET POLICY: COMPLETE REQUEST AND APPROVAL FRAMEWORK .................. 30
[etc...]
SCHEDULE 1: SECTION 8 GROUNDS DETAILED LEGAL ANALYSIS ................... 55
SCHEDULE 2: TENANT RIGHTS COMPREHENSIVE SUMMARY ......................... 65
SCHEDULE 3: DISPUTE RESOLUTION COMPLETE PATHWAYS ........................ 70
SCHEDULE 4: RENT REVIEW MARKET EVIDENCE PROCEDURES ...................... 75
SCHEDULE 5: AWAAB'S LAW HAZARD RESPONSE MATRIX .......................... 80
SCHEDULE 6: PRESCRIBED INFORMATION AND DEPOSIT PROTECTION ............... 85

================================================================================
1. DEFINITIONS AND INTERPRETATION
================================================================================

1.1 Definitions
    1.1.1 In this Agreement, unless the context otherwise requires:
          (a) "Agreement" means this periodic tenancy agreement including all schedules;
          (b) "Property" means the premises described in clause 2.3;
          (c) "Periodic Tenancy" means a tenancy continuing on a rolling basis without fixed end date;
          (d) "Section 8 Grounds" means the grounds for possession under Section 8 of the Housing Act 1988;

================================================================================
3. PERIODIC TENANCY GRANT AND LEGAL FRAMEWORK
================================================================================

3.1 Nature of Tenancy
    This is a periodic tenancy continuing on a rolling monthly basis. This tenancy
    does NOT expire on any particular date and continues indefinitely until ended
    by proper notice in accordance with statutory requirements and this agreement.
    Section 21 has been abolished and possession can only be sought under Section 8 grounds.

Focus on professional-grade sophistication emphasizing fairness, clarity, and legal compliance before disputes arise. Provide tribunal-ready documentation with complete legal frameworks and extensive procedural safeguards.`;

export const standardPrompt = premiumPrompt;
export const enhancedPrompt = premiumPrompt;

export const generateTenancyAgreementWithClaude = async (formData: Record<string, any>) => {
  const client = new Anthropic({
    apiKey: ANTHROPIC_API_KEY,
    dangerouslyAllowBrowser: true,
  });

  const baseStructure = `
COMPREHENSIVE UK TENANCY AGREEMENT STRUCTURE:

PARTIES AND PROPERTY:
- Agreement date: ${formData.startDate || '[Date]'}
- Landlord: ${formData.landlordName || '[Landlord Name]'}
- Landlord Entity Type: ${formData.landlordEntityType || 'Individual'}
${formData.companyNumber ? `- Company Registration Number: ${formData.companyNumber}` : ''}
- Landlord Address: ${formData.landlordAddress || '[Landlord Address]'}
- Tenant: ${formData.tenantName || '[Tenant Name]'}
${formData.additionalTenantNames ? `- Additional Tenants: ${formData.additionalTenantNames}` : ''}
- Tenant Address: ${formData.tenantCurrentAddress || '[Tenant Address]'}
- Property: ${formData.propertyAddress || '[Property Address]'}
- Property Description: ${formData.propertyDescription || '[Property Description]'}
${formData.isHMO === 'Yes' ? `- HMO License Number: ${formData.hmoLicenseNumber || '[HMO License]'}` : ''}
${formData.selectiveLicensing === 'Yes' ? `- Selective License Number: ${formData.selectiveLicenseNumber || '[License Number]'}` : ''}
${formData.isLeasehold === 'Yes' ? `- Superior Landlord: ${formData.superiorLandlordDetails || '[Superior Landlord Details]'}` : ''}

GUARANTOR DETAILS:
${formData.hasGuarantor === 'Yes' ? `
- Guarantor Name: ${formData.guarantorName || '[Guarantor Name]'}
- Guarantor Address: ${formData.guarantorAddress || '[Guarantor Address]'}
- Guarantor Email: ${formData.guarantorEmail || '[Guarantor Email]'}
` : '- No guarantor'}

TENANCY TERMS:
- Joint Tenancy: ${formData.isJointTenancy || 'No'}
- Commencement: ${formData.startDate || '[Commencement Date]'}
- Duration: ${formData.tenancyDuration || '[Term Length]'}
- Relationship: ${formData.landlordTenantRelationship || 'No relationship'}
- Landlord Occupancy: ${formData.landlordOccupancy || 'No'}

RENT AND PAYMENT:
- Rent: £${formData.rentAmount || '[Rent Amount]'} per ${formData.rentPaymentFrequency || 'month'}
- Payment Method: ${formData.rentPaymentMethod || 'Bank Transfer'}
- Due Date: ${formData.rentDueDate || '[Rent Due Date]'} of each period
- Late Payment Interest: ${formData.latePaymentFee || '3'}% above BoE base rate
${formData.serviceCharges === 'Yes' ? `- Service Charges: £${formData.serviceChargeAmount || '0'} per month` : ''}

DEPOSIT:
- Deposit: £${formData.depositAmount || '[Deposit Amount]'}
- Deposit Scheme: ${formData.depositScheme || 'TDS'}
- Deduction Process: ${formData.depositDeductionProcess || '[Process Details]'}

BREAK CLAUSE DETAILS:
${formData.breakClause !== 'No break clause' ? `
- Break Clause Type: ${formData.breakClause || 'Not specified'}
- Break Terms: ${formData.breakClauseTerms || 'Standard terms'}
${formData.breakClauseCustomTerms ? `- Custom Terms: ${formData.breakClauseCustomTerms}` : ''}
` : '- No break clause included'}

PROPERTY DETAILS:
- Furnishing: ${formData.furnishingStatus || 'Not specified'}
- Pets: ${formData.permitsPets || 'Not specified'}
${formData.petBondRequired === 'Yes' ? '- Pet Bond Required: Yes' : ''}
- Smoking: ${formData.smokingPolicy || 'Not specified'}
- Alterations: ${formData.alterationsPolicy || 'Not specified'}
- Permitted Use: ${Array.isArray(formData.permittedUse) ? formData.permittedUse.join(', ') : 'Residential only'}
- Utilities (Tenant Responsible): ${Array.isArray(formData.utilities) ? formData.utilities.join(', ') : 'Not specified'}
- Utilities Included in Rent: ${Array.isArray(formData.utilitiesIncluded) ? formData.utilitiesIncluded.join(', ') : 'None'}

SAFETY CERTIFICATES:
- Gas Safety Certificate: ${formData.gasSafetyCertificate || 'Not specified'}
${formData.gasSafetyCertificateDate ? `- Gas Certificate Date: ${formData.gasSafetyCertificateDate}` : ''}
- EPC Rating: ${formData.epcRating || '[Rating]'}
${formData.epcCertificateDate ? `- EPC Date: ${formData.epcCertificateDate}` : ''}
- EICR: ${formData.eicrCertificate || 'Not specified'}
${formData.eicrCertificateDate ? `- EICR Date: ${formData.eicrCertificateDate}` : ''}
- Smoke Alarms: ${formData.smokeAlarms || 'Yes'}
- Carbon Monoxide Alarms: ${formData.carbonMonoxideAlarms || 'Not applicable'}
- Fire Escape Routes: ${formData.fireEscapeRoutes || '[Details]'}
- How to Rent Guide Provided: ${formData.howToRentProvided || 'Yes'}

MAINTENANCE AND RESPONSIBILITIES:
- Landlord Repairs: ${formData.repairsResponsibility || '[Landlord Responsibilities]'}
- Tenant Repairs: ${formData.tenantRepairsResponsibility || '[Tenant Responsibilities]'}
- Inventory Process: ${formData.inventoryProcess || '[Inventory Process]'}
- Check-out Cleaning: ${formData.checkOutCleaning || 'Reasonable cleanliness expected'}

INSURANCE:
- Landlord Buildings Insurance: ${formData.landlordInsurance || 'Yes'}
- Tenant Contents Insurance: ${formData.tenantInsuranceRequired || 'Recommended'}

COMPLIANCE:
- Right to Rent Check: ${formData.rightToRentCheck || 'Completed'}
- GDPR Consent: ${formData.dataProtectionConsent || 'Yes'}
${formData.knownIssues ? `- Known Issues: ${formData.knownIssues}` : ''}

Generate a comprehensive UK tenancy agreement incorporating EVERY detail from above with extensive legal provisions, detailed explanations, and comprehensive clauses suitable for professional use.`;

  const premiumResponse = await client.messages.create({
    model: 'claude-sonnet-4-6',
    max_tokens: 32000,
    temperature: 0.2,
    system: premiumPrompt,
    messages: [
      {
        role: 'user',
        content: `Generate a comprehensive PREMIUM VERSION UK tenancy agreement (60-70 pages, approximately 26,000 words) incorporating EVERY single detail from the form data with exhaustive legal documentation, schedules and appendices, professional court-ready formatting, and complete legal framework. Use this data: ${baseStructure}`
      }
    ]
  });

  const content = premiumResponse.content[0].type === 'text' ? premiumResponse.content[0].text : '';

  return {
    standard: '',
    enhanced: '',
    premium: content
  };
};

export const generateDocumentWithClaude = async ({ documentType, formData, config }: {
  documentType: string;
  formData: Record<string, any>;
  config: any;
}) => {
  const client = new Anthropic({
    apiKey: ANTHROPIC_API_KEY,
    dangerouslyAllowBrowser: true,
  });

  const baseData = Object.entries(formData)
    .map(([key, value]) => `${key}: ${value}`)
    .join('\n');

  const documentContext = `
Document Type: ${documentType}
Form Data:
${baseData}

Generate a comprehensive UK legal document that is fully compliant with current UK law and regulations.
`;

  const premiumResponse = await client.messages.create({
    model: 'claude-sonnet-4-6',
    max_tokens: 32000,
    temperature: 0.2,
    system: config.prompts.premium,
    messages: [
      {
        role: 'user',
        content: `${documentContext}\n\nGenerate a comprehensive PREMIUM VERSION of this document. Include EVERY single detail from the form data with exhaustive legal documentation, extensive schedules and appendices, comprehensive procedures, detailed precedents, professional court-ready formatting, extensive guidance notes, and complete legal framework.`
      }
    ]
  });

  const content = premiumResponse.content[0].type === 'text' ? premiumResponse.content[0].text : '';

  return {
    standard: '',
    enhanced: '',
    premium: content
  };
};
